// AssetModelActions — the product's ONE registry and its overlays (ADR-036 §7 —
// the four kinds; mirrors the exemplar `pages/teams/TeamActions.tsx`).
//
//   • `assetModelActs` is the PURE registry — rows with a kind, a section and a
//     `show` predicate over the status and the closed-over facts; the shape test
//     (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useAssetModelActions` resolves it for a product and returns the ACTS the
//     peek composes from and the OVERLAYS (every dialog and drawer, mounted ONCE
//     beside the peek).
//
// A product is PEEK-ONLY (no full page — ADR-065): its edits live on their cards
// (`ContextPanel pageless` — ADR-036 §7, exception 1), each link the verb alone,
// the card's title naming what: Identity → Edit (the identity & specs drawer),
// Connectivity → Edit (capabilities & interfaces — a networked product's),
// Documents → Manage (the documents face). The door: Disable product (confirmed) ·
// Enable product. Every act is a LOCAL product's ("Added here") — a catalog
// record is the catalog's to change, so it offers none (its body says so) — and
// the gate is `asset.models:manage`.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetModelProjectItem } from '@ems-app/schemas/catalog/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../components/patterns/entityRegistry.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { icons } from '../../components/ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useUpdateAssetModel } from '../../hooks/useAssetModels.js';
import { showsDeviceHalf } from '../../components/patterns/catalogDisplay.js';
import { ModelFilesDrawer } from './ModelFilesDrawer.js';
import { ProductDeviceDrawer, ProductIdentityDrawer } from './ProductEditDrawers.js';
import { useQuickSave } from '../../components/patterns/forms/useQuickSave.js';

export type AssetModelStatus = 'offered' | 'not-offered';

/** What each act does — the hook wires these to its overlays. */
export interface AssetModelActHandlers {
  editIdentity: () => void;
  editDevice: () => void;
  manageDocuments: () => void;
  disable: () => void;
  enable: () => void;
}

/** The closed-over facts the rows read beside the status. */
export interface AssetModelActFacts {
  /** Added here — neither a catalog record nor a bare category row. */
  local: boolean;
  /** A networked product (the device half shows — `showsDeviceHalf`). */
  device: boolean;
}

/** The product's registry — pure: the rows, their kinds, sections and predicates. */
export function assetModelActs(h: AssetModelActHandlers, facts: AssetModelActFacts): ActionItem<AssetModelStatus>[] {
  const local = () => facts.local;
  return [
    // The edits — each on its card in the peek, the verb alone (the card's title names what).
    { id: 'edit-identity', kind: 'edit', section: 'identity', labelKey: 'common:entity.edit', icon: icons.edit, run: { handler: h.editIdentity }, gate: 'asset.models:manage', order: 10, show: local },
    { id: 'edit-device', kind: 'edit', section: 'connectivity', labelKey: 'common:entity.edit', icon: icons.devices, run: { handler: h.editDevice }, gate: 'asset.models:manage', order: 20, show: () => facts.local && facts.device },
    { id: 'manage-documents', kind: 'edit', section: 'documents', labelKey: 'common:entity.manage', icon: icons.file, run: { handler: h.manageDocuments }, gate: 'asset.models:manage', order: 30, show: local },
    // The door — the lifecycle.
    { id: 'disable', kind: 'lifecycle', labelKey: 'store.disable', icon: icons.statusBlocked, run: { handler: h.disable }, gate: 'asset.models:manage', order: 10, show: (ctx) => facts.local && ctx.status === 'offered' },
    { id: 'enable', kind: 'lifecycle', labelKey: 'store.enable', icon: icons.restore, run: { handler: h.enable }, gate: 'asset.models:manage', order: 10, show: (ctx) => facts.local && ctx.status === 'not-offered' },
  ];
}

export interface AssetModelActions {
  /** The resolved set — what the peek composes from (`ContextPanel acts pageless` · each card's `SectionAct`). */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE beside the peek. */
  overlays: ReactNode;
}

type Overlay = 'identity' | 'device' | 'documents' | 'disable';

/** The product's acts. Takes the product while it still loads (the peek keeps one
 *  panel across the load — nothing until it is there). */
export function useAssetModelActions(projectId: string, model: AssetModelProjectItem | undefined): AssetModelActions {
  const { t } = useTranslation('catalog');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const update = useUpdateAssetModel(projectId, model?.id ?? '');
  // Enable is low-stakes and direct (no dialog) — a quick save: a refusal said once, the page re-read.
  const quickUpdate = useQuickSave(update, { key: 'model-enable' });
  // The open overlay, held with the product it was opened for — the peek's hook
  // outlives a peek→peek switch (the successor link), so an overlay open on one
  // product never reopens on the next.
  const [open, setOpen] = useState<{ overlay: Overlay; modelId: string } | null>(null);

  if (!model) return { acts: [], overlays: null };
  const opened = (overlay: Overlay) => open?.overlay === overlay && open.modelId === model.id;
  const show = (overlay: Overlay) => () => setOpen({ overlay, modelId: model.id });
  const close = () => setOpen(null);

  const acts = resolveActs(
    assetModelActs(
      {
        editIdentity: show('identity'),
        editDevice: show('device'),
        manageDocuments: show('documents'),
        disable: show('disable'),
        enable: () => void quickUpdate.save({ is_disabled: false }),
      },
      { local: !model.is_canonical && !model.is_generic, device: showsDeviceHalf(model) },
    ),
    { can, entitlements, status: model.is_disabled ? 'not-offered' : 'offered' },
    (key) => t(key),
  );

  const overlays = (
    <>
      {opened('identity') && <ProductIdentityDrawer projectId={projectId} model={model} open onClose={close} />}
      {opened('device') && <ProductDeviceDrawer projectId={projectId} model={model} open onClose={close} />}
      {opened('documents') && <ModelFilesDrawer projectId={projectId} model={model} open onClose={close} />}
      <ConfirmDialog
        open={opened('disable')}
        title={t('store.disableTitle')}
        description={t('store.disableBody')}
        confirmLabel={t('store.disableConfirm')}
        tone="default"
        onConfirm={async () => {
          await update.mutateAsync({ is_disabled: true });
        }}
        onClose={close}
      />
    </>
  );

  return { acts, overlays };
}
